import React, { useState, useEffect, useRef } from "react";
import { View, StyleSheet, ScrollView, Platform, Text, Dimensions, RefreshControl } from "react-native";
import { useSelector, useDispatch } from "react-redux";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { AppDispatch } from "../../../store";
import { useToast } from "../../../components/LhToastContainer";
import NHOWelcome from "../components/NHOWelcome";
import NHOBegin from "../components/NHOBegin";
import NHOFilters from "../components/NHOFilters";
import NHOResources from "../components/NHOResources";
import NHODashboardTable from "../components/NHODashboardTable";
import TouchpointDetailsOverlay from "../components/TouchpointDetailsOverlay";
import MobileFilterOverlay from "../../../components/MobileFilterOverlay";
import { useTheme } from "../../../theme";

import {
  setSelectedFilters,
  setModalVisibility,
  fetchNHOEmployees,
  clearSuccessMessage,
  setFilteredTouchpoints,
  setTouchpoints,
  setLoading,
  setError as setNHOError,
  resetFilters,
} from "../../../store/slices/nhoSlice";
import { createApiClient, apiClient } from "../../../api/apiClient";
import { 
  TOUCHPOINT_OPTIONS,
  TOUCHPOINT_DISPLAY_NAMES,
  TABLE_TOUCHPOINT_OPTIONS
} from '../../../constants';
import { getPeriodOptions, getWeekOptions, getYearOptions } from "../../../utils/filterOptions";

// Define props interface
interface DashboardProps {
  onAdminClick?: () => void;
  onBeginOnboardingClick?: (
    destination?: string,
    filters?: { employee: string; observationType: string }
  ) => void;
  onManageEmployeesClick?: () => void;
  onEmployeeProfileClick?: (employeeId: string) => void;
  initialMessage?: string;
}

const Dashboard: React.FC<DashboardProps> = ({
  onAdminClick,
  onBeginOnboardingClick,
  onManageEmployeesClick,
  onEmployeeProfileClick,
  initialMessage,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDarkTheme = resolvedTheme === "dark";
  // Local state for filters (will be moved to Redux when applied)
  const [filters, setFilters] = useState({
    employee: "",
    observationType: "",
  });
  const [showValidation, setShowValidation] = useState(false);

  // To track if the component has initialized properly
  const [initialized, setInitialized] = useState(false);

  // Ref for ScrollView to enable auto-scroll to top
  const scrollViewRef = useRef<ScrollView>(null);

  // Mobile filter state for table filters (following ManageEmployeeUpdates pattern)
  const [showTableFilterModal, setShowTableFilterModal] = useState(false);
  const [tempTableFilters, setTempTableFilters] = useState<any>({});

  // Compute iOS mobile boolean inside the component
  const windowWidth = Dimensions.get("window").width;
  const isMobileIos = Platform.OS === "ios" && windowWidth < 768;
  const [refreshing, setRefreshing] = useState(false);

  // Redux state and dispatch
  const dispatch = useDispatch<AppDispatch>();
  const toast = useToast();

  const {
    employees,
    observationTypes,
    isModalVisible,
    employeeLoadError,
    successMessage,
  } = useSelector((state: any) => state.nho);
  const {
    employees: { data: appEmployees = [] } = { data: [] },
    selectedLocation,
  } = useSelector((state: any) => state.appData);

  // Get table filter options and handlers from Redux
  const { selectedFilters: tableSelectedFilters, touchpoints = [] } = useSelector(
    (state: any) => state.nho
  );

  // Function to format email to name (for Created By filter)
  const formatEmailToName = (email: string): string => {
    if (!email) return "";
    const namePart = email.split("@")[0];
    const names = namePart.split(".");
    return names
      .map((name) => name.charAt(0).toUpperCase() + name.slice(1))
      .join(" ")
      .replace(/Contractor$/, "");
  };

  // Function to get unique creators from touchpoints
  const getUniqueCreators = (
    touchpoints: any[]
  ): Array<{ id: string; displayText: string }> => {
    const uniqueEmails = Array.from(
      new Set(touchpoints.map((tp) => tp.Created_By))
    );
    return uniqueEmails
      .filter((email) => email)
      .map((email) => ({
        id: email,
        displayText: formatEmailToName(email),
      }))
      .sort((a, b) => a.displayText.localeCompare(b.displayText));
  };

  // Prepare table filter options
  const touchPointOptions = TABLE_TOUCHPOINT_OPTIONS;

  const tableEmployeeOptions = (() => {
    const nhoEmployees = employees || [];
    
    return nhoEmployees.length > 0
      ? nhoEmployees.map((emp: any) => ({
          id: emp.GPID || "",
          displayText:
            emp.Full_Name || `${emp.First_Name || ""} ${emp.Last_Name || ""}`,
        }))
      : [];
  })();

  const actionOptions = [
    { id: "In Progress", displayText: "In Progress" },
    { id: "Completed", displayText: "Completed" },
  ];

  const tableFilterOptions = [
    {
      key: "employee",
      label: "Select an Employee",
      selectedOptionId: tableSelectedFilters?.employeeGpid || "",
      options: tableEmployeeOptions,
    },
    {
      key: "touchpoint",
      label: "Type of Touch Point",
      selectedOptionId: tableSelectedFilters?.touchpoint || "",
      options: touchPointOptions,
    },
    {
      key: "period",
      label: "Period",
      selectedOptionId: tableSelectedFilters?.period || "",
      options: getPeriodOptions(),
    },
    {
      key: "Week",
      label: "Week",
      selectedOptionId: tableSelectedFilters?.Week || "",
      options: getWeekOptions(),
    },
    {
      key: "year",
      label: "Year",
      selectedOptionId: tableSelectedFilters?.year || "",
      options: getYearOptions(),
    },
    {
      key: "createdBy",
      label: "Created By",
      selectedOptionId: tableSelectedFilters?.createdBy || "",
      options: getUniqueCreators(touchpoints),
    },
    {
      key: "Action",
      label: "Status",
      selectedOptionId: tableSelectedFilters?.status || "",
      options: actionOptions,
    },
  ];

  // Initialize component and ensure data is properly loaded
  useEffect(() => {
    if (!initialized && Array.isArray(appEmployees)) {
      setInitialized(true);
      // You could dispatch any additional initialization actions here if needed
    }
  }, [appEmployees, initialized]);

  // Fetch NHO employees when component loads or location changes
  useEffect(() => {
    if (selectedLocation?.displayText) {
      dispatch(fetchNHOEmployees(selectedLocation.displayText));
    }
  }, [dispatch, selectedLocation?.displayText]);

  // Handle employee loading errors - now using notification bar instead of toast
  // No useEffect needed as we'll show the error directly in the render

  // Handle success message with auto-clear timer and auto-scroll
  useEffect(() => {
    if (successMessage) {
      // Scroll to top to show the success message
      scrollViewRef.current?.scrollTo({
        y: 0,
        animated: true,
      });

      // Auto-clear the success message after 5 seconds
      const timer = setTimeout(() => {
        dispatch(clearSuccessMessage());
      }, 5000); // Clear after 5 seconds

      // Cleanup timer on unmount or when dependencies change
      return () => clearTimeout(timer);
    }
  }, [successMessage, dispatch]);

  // Function to handle navigation to Admin page
  const handleAdminClick = () => {
    if (onAdminClick) {
      onAdminClick();
    }
  };

  // Function to open the Begin modal when clicked from the Welcome component
  const handleBeginClick = () => {
    // Reset filters to ensure a clean state when opening the modal
    setFilters({
      employee: "",
      observationType: "",
    });
    // Open the modal to select options
    dispatch(setModalVisibility(true));
  };

  // Function to close the modal
  const closeModal = () => {
    dispatch(setModalVisibility(false));
    setShowValidation(false);
  };

  // Function to save filters and proceed with onboarding
  const handleSaveFilters = () => {
    console.log("Saving filters:", filters);
    console.log("onBeginOnboardingClick available:", !!onBeginOnboardingClick);

    // Check if any required fields are empty
    const requiredFieldsEmpty = Object.values(filters).some(
      (value) => value === ""
    );

    if (requiredFieldsEmpty) {
      // Show validation errors and don't proceed
      setShowValidation(true);
    } else {
      // All required fields are filled, proceed
      setShowValidation(false);

      // Resolve full employee info from nho employees list
      const selectedEmployee = employees.find(
        (emp: any) => String(emp.GPID) === String(filters.employee)
      );
      const enrichedFilters = {
        ...filters,
        employee:
          selectedEmployee?.Full_Name ||
          `${selectedEmployee?.First_Name || ""} ${selectedEmployee?.Last_Name || ""}`.trim() ||
          filters.employee,
        employeeFirstName: selectedEmployee?.First_Name || "",
        employeeLastName: selectedEmployee?.Last_Name || "",
        employeeGpid: filters.employee,
      };

      // Save selected filters to Redux state
      dispatch(setSelectedFilters(enrichedFilters));

      // Check if we need to navigate to a specific touchpoint page
      let destination = "";
      if (filters.observationType === "pulse_check") {
        destination = "PulseCheckTouchPoint";
      } else if (
        ["30", "60", "90", "preferred_method"].includes(filters.observationType)
      ) {
        destination = "SpecificPeriodTouchPoint";
      }

      // Close modal
      closeModal();

      // Navigate if a destination was determined
      if (destination && onBeginOnboardingClick) {
        console.log(`Navigating to: ${destination} with filters:`, filters);
        onBeginOnboardingClick(destination, filters);
      } else {
        // If no specific destination, just close the modal
        console.log("No specific destination, closing modal");
        console.log("Filters saved:", filters);
        console.log("Destination:", destination);
        console.log("onBeginOnboardingClick function:", onBeginOnboardingClick);
      }
    }
  };

  // Prepare dropdown options
  const employeeOptions = (() => {
    // If there's an employee load error, disable the dropdown
    if (employeeLoadError) {
      return [{ id: "", displayText: "Unable to load employees" }];
    }

    // Use NHO employees from Redux if available, otherwise fall back to appEmployees
    const employeeSource = employees.length > 0 ? employees : appEmployees;

    if (Array.isArray(employeeSource) && employeeSource.length > 0) {
      // Filter employees to only include those with "Active" status
      const activeEmployees = employeeSource.filter(
        (emp: any) =>
          emp.Status === "Active" ||
          emp.status === "Active" ||
          emp.STATUS === "Active"
      );

      return [
        ...activeEmployees.map((emp: any) => ({
          id: emp.GPID || "",
          displayText:
            emp.Full_Name || `${emp.First_Name || ""} ${emp.Last_Name || ""}`,
        })),
      ];
    }

    return [];
  })();

  const observationTypeOptions = Array.isArray(observationTypes)
    ? [
        { id: "", displayText: "Select Onboarding Type" },
        ...observationTypes.map((type: any) => ({
          id: type.id || "",
          displayText: type.name || "Unknown",
        })),
      ]
    : [{ id: "", displayText: "Select Onboarding Type" }];

  const dropdownOptions = {
    employees: employeeOptions,
    observationTypes: observationTypeOptions,
  };

  // Function to render filter dropdowns
  const renderDropdowns = () => {
    return (
      <NHOFilters
        dropdownOptions={dropdownOptions}
        filters={filters}
        setFilters={setFilters}
        showValidation={showValidation}
      />
    );
  };

  const handleManageEmployeesClick = () => {
    if (onManageEmployeesClick) {
      onManageEmployeesClick();
    }
  };
  // Function to handle navigation to Employee Profile
  const handleEmployeeProfileClick = (employeeId: string) => {
    // Don't navigate if there's an employee load error
    if (employeeLoadError) {
      toast.showToast({
        header: "Feature Unavailable",
        subtext:
          "Employee Profile is currently unavailable due to data loading issues.",
        variant: "warning",
        position: "top-right",
      });
      return;
    }

    if (onEmployeeProfileClick) {
      onEmployeeProfileClick(employeeId);
    }
  };

  // State for touchpoint details overlay
  const [isOverlayVisible, setIsOverlayVisible] = useState(false);
  const [selectedTouchpoint, setSelectedTouchpoint] = useState<any>(null);

  // Handle row click for the touchpoint table
  const handleTouchpointRowClick = (rowData: any) => {
    console.log("Dashboard handleTouchpointRowClick called with:", rowData);

    // Check if this is a navigation request from Employee Name cell click
    if (
      rowData.shouldNavigateToTouchpoint &&
      rowData.destination &&
      rowData.filters
    ) {
      console.log(
        "Navigation requested to:",
        rowData.destination,
        "with filters:",
        rowData.filters
      );

      if (onBeginOnboardingClick) {
        onBeginOnboardingClick(rowData.destination, rowData.filters);
      } else {
        console.warn("onBeginOnboardingClick not available for navigation");
      }
      return;
    }

    // Regular overlay display for other cell clicks
    setSelectedTouchpoint(rowData);
    setIsOverlayVisible(true);
  };

  // Close the overlay
  const closeOverlay = () => {
    setIsOverlayVisible(false);
    setSelectedTouchpoint(null);
  };

  // Sync tempTableFilters with tableSelectedFilters when modal opens
  useEffect(() => {
    if (showTableFilterModal) {
      setTempTableFilters(tableSelectedFilters || {});
    }
  }, [showTableFilterModal, tableSelectedFilters]);

  // Function to fetch touchpoints with filters (from NHODashboardTable logic)
  const fetchTouchpointsWithFilters = async (filtersOverride?: any) => {
    const filtersToUse = filtersOverride || tableSelectedFilters || {};
    const location = selectedLocation?.displayText || selectedLocation?.name || "";
    if (!location) {
      dispatch(setNHOError("No location selected"));
      return;
    }

    dispatch(setLoading(true));

    try {
      const api = createApiClient();
      let url = `/new-hire-onboarding?location=${encodeURIComponent(location)}`;

      // Add filters to the URL if they have valid values
      if (filtersToUse.employeeGpid) {
        url += `&gpid=${filtersToUse.employeeGpid}`;
      }
      if (filtersToUse.touchpoint) {
        url += `&touchpoint=${filtersToUse.touchpoint}`;
      }
      if (filtersToUse.period) {
        url += `&period=${filtersToUse.period}`;
      }
      if (filtersToUse.Week) {
        url += `&week=${filtersToUse.Week}`;
      }
      if (filtersToUse.year) {
        url += `&year=${filtersToUse.year}`;
      }
      if (filtersToUse.createdBy) {
        url += `&createdBy=${encodeURIComponent(filtersToUse.createdBy)}`;
      }
      if (filtersToUse.status && filtersToUse.status !== "") {
        url += `&status=${encodeURIComponent(filtersToUse.status)}`;
      }

      const response = await apiClient(api, url, "GET");

      const transformedData = (response.data || []).map((item: any) => ({
        ...item,
        type: item.Type_of_Touchpoint || "unknown",
      }));

      dispatch(setTouchpoints(transformedData));
      dispatch(setFilteredTouchpoints(transformedData));
      dispatch(setNHOError(null));
    } catch (err) {
      console.error("Error fetching touchpoints data with filters:", err);
      dispatch(setNHOError("Failed to fetch touchpoints data"));
    } finally {
      dispatch(setLoading(false));
    }
  };

  // Mobile proceed handler for table filters
  const handleTableMobileProceed = (selectedFiltersFromModal: Partial<Record<string, any>>) => {
    console.log("[Dashboard Mobile Filter] Proceeding with filters:", selectedFiltersFromModal);

    // Build the payload for Redux
    const payload: any = {};

    Object.keys(selectedFiltersFromModal).forEach((key) => {
      const value = selectedFiltersFromModal[key];
      
      if (key === "employee" && value) {
        const employeeData = appEmployees.find(
          (emp: any) => String(emp.GPID) === value.id || emp.Full_Name === value.displayText
        );
        if (employeeData) {
          payload.employee = employeeData.Full_Name || value.displayText || "";
          payload.employeeFirstName = employeeData.First_Name || "";
          payload.employeeLastName = employeeData.Last_Name || "";
          payload.employeeGpid = employeeData.GPID || "";
        } else {
          payload.employee = value.displayText || "";
          payload.employeeGpid = value.id || "";
        }
      } else if (key === "createdBy" && value) {
        payload.createdBy = value.id || value;
      } else if (key === "Action" && value) {
        const statusValue = value.displayText === "All" ? "" : value.displayText;
        payload.status = statusValue;
      } else if (value && value.id !== undefined) {
        payload[key] = value.id || "";
      } else if (value) {
        payload[key] = value;
      }
    });

    // Dispatch to Redux
    dispatch(setSelectedFilters(payload));

    // Immediately apply filters
    fetchTouchpointsWithFilters(payload);

    // Close modal
    setShowTableFilterModal(false);
    setTempTableFilters({});
  };

  const onRefresh = async () => {
    setRefreshing(true);
    const timeout = new Promise<void>((_, reject) =>
      setTimeout(() => reject(new Error('refresh_timeout')), 3000)
    );
    try {
      await Promise.race([fetchTouchpointsWithFilters(), timeout]);
    } catch {
      // timeout or error — silently cancel
    } finally {
      setRefreshing(false);
    }
  };

  // Show filter modal for iPhone only at Dashboard level
  if (showTableFilterModal && isMobileIos) {
    return (
      <MobileFilterOverlay
        visible={showTableFilterModal}
        onDismiss={() => {
          setShowTableFilterModal(false);
          setTempTableFilters({});
        }}
        filters={tableFilterOptions}
        onFilterChange={(key: string, value: any) => {
          setTempTableFilters((prev: any) => ({
            ...prev,
            [key]: value,
          }));
        }}
        onProceed={handleTableMobileProceed}
        onCancel={() => {
          setShowTableFilterModal(false);
          setTempTableFilters({});
        }}
        currentFilters={tempTableFilters}
      />
    );
  }

  return (
    <View
      style={[
        styles.container,
        isDarkTheme && { backgroundColor: colors.background.page },
      ]}
      testID="nho-dashboard"
    >
      <ScrollView
        ref={scrollViewRef}
        style={[
          styles.contentScrollView,
          isDarkTheme && { backgroundColor: colors.background.page },
        ]}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            tintColor={colors.brand.primary}
            colors={[colors.brand.primary]}
            title="Refreshing..." 
          />
        }
      >
        {initialMessage && (
          <View
            style={[
              styles.messageContainer,
              isDarkTheme && {
                backgroundColor: colors.background.surface,
                borderLeftColor: colors.brand.primary,
              },
            ]}
          >
            <Text
              style={[
                styles.messageText,
                isDarkTheme && { color: colors.text.primary },
              ]}
            >
              {initialMessage}
            </Text>
          </View>
        )}
        {successMessage && (
          <View
            style={[
              styles.successMessageContainer,
              isDarkTheme && {
                backgroundColor: colors.background.surface,
                borderColor: colors.border.default,
              },
            ]}
          >
            <MaterialIcons
              name="check-circle"
              size={24}
              color={isDarkTheme ? colors.text.primary : "#008242"}
              style={styles.successIcon}
            />
            <Text
              style={[
                styles.successMessageText,
                isDarkTheme && { color: colors.text.primary },
              ]}
            >
              {successMessage}
            </Text>
          </View>
        )}
        {employeeLoadError && (
          <View
            style={[
              styles.errorMessageContainer,
              isDarkTheme && {
                backgroundColor: colors.background.surface,
                borderColor: colors.border.default,
              },
            ]}
          >
            <MaterialIcons
              name="error"
              size={24}
              color={isDarkTheme ? colors.text.primary : "#d32f2f"}
              style={styles.errorIcon}
            />
            <Text
              style={[
                styles.errorMessageText,
                isDarkTheme && { color: colors.text.primary },
              ]}
            >
              Error Loading Employees: {employeeLoadError}
            </Text>
          </View>
        )}
        <NHOWelcome
          onAdminClick={handleAdminClick}
          onBeginClick={handleBeginClick}
          onEmployeeProfileClick={handleEmployeeProfileClick}
          testID="nho-welcome"
        />
        <NHOResources
          onManageEmployeesClick={handleManageEmployeesClick}
          testID="nho-resources"
          showFilterButton={isMobileIos}
          onFilterButtonPress={() => setShowTableFilterModal(true)}
        />

        {/* NHO Dashboard Table */}
        <NHODashboardTable
          onRowClick={handleTouchpointRowClick}
          locationName={selectedLocation?.name}
          showFilters={true}
          showFilterModal={showTableFilterModal}
          setShowFilterModal={setShowTableFilterModal}
          tempFilters={tempTableFilters}
          setTempFilters={setTempTableFilters}
          isMobileIos={isMobileIos}
        />
      </ScrollView>
      {/* Begin Onboarding Modal */}
      {initialized && (
        <NHOBegin
          isModalVisible={isModalVisible}
          closeModal={closeModal}
          renderDropdowns={renderDropdowns}
          handleSaveFilters={handleSaveFilters}
          filters={filters}
        />
      )}
      {/* Touchpoint Details Overlay */}
      <TouchpointDetailsOverlay
        visible={isOverlayVisible}
        onDismiss={closeOverlay}
        touchpoint={selectedTouchpoint}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#F5F9FF",
    paddingHorizontal: Platform.OS === "web" ? "3%" : 16,
    paddingTop: 16,
  },
  contentScrollView: {
    flex: 1,
  },
  messageContainer: {
    backgroundColor: "#e6f7ff",
    padding: 16,
    borderRadius: 8,
    marginTop: 16,
    marginBottom: 8,
    borderLeftWidth: 4,
    borderLeftColor: "#1890ff",
  },
  messageText: {
    fontSize: 16,
    color: "#0066CC",
    fontWeight: "500",
  },
  successMessageContainer: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#d4edda",
    borderColor: "#c3e6cb",
    borderWidth: 1,
    borderRadius: 6,
    padding: 16,
    marginBottom: 16,
    marginTop: 16,
  },
  successIcon: {
    marginRight: 8,
  },
  successMessageText: {
    color: "#155724",
    fontSize: 16,
    flex: 1,
  },
  errorMessageContainer: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#f8d7da",
    borderColor: "#f5c6cb",
    borderWidth: 1,
    borderRadius: 6,
    padding: 16,
    marginBottom: 16,
    marginTop: 16,
  },
  errorIcon: {
    marginRight: 8,
  },
  errorMessageText: {
    color: "#721c24",
    fontSize: 16,
    flex: 1,
  },
});

export default Dashboard;
